// The identifier the backend's single-device rule compares (Users.DeviceName).
//
// ⚠️ THREE APPS, ONE COLUMN, AND THEY DID NOT AGREE.
//
// All of these write Users.DeviceName for the same account, and the gate is a
// plain string comparison — so a value one app writes must be one the others
// recognise, or the next app to open shows "Unauthorised Device" ON THE SAME
// PHONE:
//
//   smartify-automation-flutter   getDeviceInfo().getDeviceDetails()
//                                 -> build.MODEL      e.g. "SM-G991B"
//   SmartLifeIndia-flutter        linker.dart getDeviceId()
//                                 -> androidInfo.id   e.g. "TP1A.220624.014"
//
// Note getDeviceInfo also computes `identifier = build.id`, but its function
// RETURNS DeviceName (the model) — the id is dead code. That is easy to misread.
//
// WHAT THIS APP DOES
// It writes the MODEL, matching smartify-automation-flutter, because that app
// stays in service while SmartLifeIndia-flutter is retired by this one.
//
// It ACCEPTS either form (see acceptableDeviceIds), so an account currently
// bound by the old SmartLifeIndia build still passes without forcing an OTP.
// Only when nothing matches is the user asked to re-bind.
//
// ⚠️ NEITHER VALUE IS UNIQUE TO A PHONE. The model is shared by every handset of
// that type, and Build.ID by every device on that firmware — so "single device"
// is far weaker than it sounds. That is pre-existing behaviour in both Flutter
// apps; matching it is the point here. Tightening it means changing every app at
// once, plus a migration for accounts already bound to a loose value.

import { Device } from '@capacitor/device';
import { isNativePlatform } from '../platform';

const KEY = 'sli_device_id';

let cachedPrimary = null;
let cachedAll = null;

// "…; SM-G991B Build/TP1A.220624.014; wv…" -> "TP1A.220624.014"
export function buildIdFromUserAgent(ua) {
  const m = /\bBuild\/([^);\s]+)/.exec(String(ua || ''));
  return m ? m[1] : null;
}

// "…Android 13; SM-G991B Build/…" -> "SM-G991B"
// Fallback only — Device.getInfo().model is authoritative and is tried first.
export function modelFromUserAgent(ua) {
  const m = /Android\s+[\d.]+;\s*([^;)]+?)\s+Build\//.exec(String(ua || ''));
  return m ? m[1].trim() : null;
}

function webFallbackId() {
  let id = null;
  try { id = localStorage.getItem(KEY); } catch (e) { /* ignore */ }
  if (id) return id;
  id = (window.crypto && window.crypto.randomUUID)
    ? window.crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try { localStorage.setItem(KEY, id); } catch (e) { /* ignore */ }
  return id;
}

// Every identifier this phone could legitimately be known by, most-preferred
// first. The gate treats a stored DeviceName matching ANY of these as this
// device, which is what lets the apps coexist.
export async function acceptableDeviceIds() {
  if (cachedAll) return cachedAll;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const ids = [];

  if (isNativePlatform()) {
    try {
      const info = await Device.getInfo();
      if (info && info.model) ids.push(String(info.model));   // == build.model
    } catch (e) { /* fall back to the UA */ }

    const uaModel = modelFromUserAgent(ua);
    if (uaModel && !ids.includes(uaModel)) ids.push(uaModel);

    const buildId = buildIdFromUserAgent(ua);                  // == androidInfo.id
    if (buildId && !ids.includes(buildId)) ids.push(buildId);

    // Last resort so login still works on a device we can't identify.
    if (ids.length === 0) {
      try {
        const idInfo = await Device.getId();
        const v = idInfo && (idInfo.identifier || idInfo.uuid);
        if (v) ids.push(String(v));
      } catch (e) { /* ignore */ }
    }
  }

  if (ids.length === 0) ids.push(webFallbackId());

  cachedAll = ids;
  return cachedAll;
}

// The value WRITTEN when binding this install — the model, to match
// smartify-automation-flutter.
export async function getDeviceId() {
  if (cachedPrimary) return cachedPrimary;
  const ids = await acceptableDeviceIds();
  cachedPrimary = ids[0];
  return cachedPrimary;
}

export function peekCachedDeviceId() {
  return cachedPrimary;
}
